import React from 'react';
import { Shield, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { PROFILE_RESTRICTIONS } from '../../constants/mediscribe';

/**
 * PatientLimitationsHelper - Suggested limitations helper for Patient mode
 * Extracted from App.js for maintainability
 */
export const PatientLimitationsHelper = ({
  generateLimitations,
  setGenerateLimitations,
  selectedRestriction,
  setSelectedRestriction,
  setNotes
}) => {
  const restriction = PROFILE_RESTRICTIONS.find(r => r.value === selectedRestriction);

  return (
    <div className="p-3 bg-green-500/5 border border-green-500/30 rounded-lg hover:bg-green-500/10 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-green-500" />
          <span className="text-green-400 font-medium text-sm">Suggested Limitations</span>
          <Badge variant="outline" className="text-[10px] bg-green-500/20 text-green-400 border-green-500/30">Helper</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="include-limitations" 
            checked={generateLimitations} 
            onCheckedChange={setGenerateLimitations} 
            className="h-4 w-4"
            data-testid="checkbox-limitations" 
          />
          <label htmlFor="include-limitations" className="text-xs text-muted-foreground cursor-pointer">
            Include in letter
          </label>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-2">Select your condition to see suggested limitations:</p>
      <Select value={selectedRestriction} onValueChange={setSelectedRestriction}>
        <SelectTrigger className="h-8 text-xs bg-input" data-testid="limitation-select">
          <SelectValue placeholder="Select condition type..." />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Physical Conditions</SelectLabel>
            {['upper_extremity', 'lower_extremity', 'back_limitation'].map(v => {
              const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
              return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
            })}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>Medical/Recovery</SelectLabel>
            {['post_surgical', 'respiratory', 'cardiac', 'pregnancy', 'postpartum'].map(v => {
              const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
              return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
            })}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>Neurological/Mental Health</SelectLabel>
            {['concussion_mTBI', 'migraines', 'behavioral_health', 'sleep_disorder', 'medication_adjustment', 'trauma_recovery'].map(v => {
              const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
              return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
            })}
          </SelectGroup>
        </SelectContent>
      </Select>
      
      {restriction && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-green-400">{restriction.label}</span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{restriction.duration}</Badge>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedRestriction('')}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="bg-background/50 rounded p-2 space-y-1 max-h-[100px] overflow-y-auto custom-scrollbar">
            {restriction.restrictions.map((r, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <span className="text-green-400">•</span>
                <span className="text-foreground">{r}</span>
              </div>
            ))}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
            onClick={() => {
              const limitationsText = restriction.restrictions.join('\n- ');
              setNotes(prev => prev ? `${prev}\n\nRequested limitations:\n- ${limitationsText}` : `Requested limitations:\n- ${limitationsText}`);
            }}
          >
            <Plus className="w-3 h-3 mr-1" />Add to Description
          </Button>
        </div>
      )}
    </div>
  );
};

export default PatientLimitationsHelper;
